import { AnyEventObject, EventData, interpret, State, StateConfig } from 'xstate';
import stateMachine, { Context } from './stateMachine';
import stepRouteMapping from './stepRouteMapping';
import VueRouter from 'vue-router';
import VIEWS_NAMES from '@/router/viewsNames';

const pipeline = interpret(stateMachine);
const transitionListeners: Function[] = [];

function getCurrentStep(state: State<Context>): string {
  const [currentStateMeta]: { step: string }[] = Object.values(state.meta);

  return currentStateMeta.step;
}

function navigateToStep(router: VueRouter, state: State<Context>) {
  const currentStep = getCurrentStep(state);

  const stepRoute = stepRouteMapping[currentStep];

  const currentRoute = router.currentRoute.path;

  if (stepRoute && stepRoute !== currentRoute) router.push(stepRoute);
}

function loadPreviousState(previousState: StateConfig<Context, AnyEventObject>): State<Context> {
  const state = State.create<Context>(previousState);
  return stateMachine.resolveState(state);
}

function forcePipelineStop(router: VueRouter) {
  pipeline.stop();
  router.push({ name: VIEWS_NAMES.HOME });
}

function onTransitionHandler(router: VueRouter, currentInstance: string) {
  return (state: State<Context>) => {
    const instanceOnStorage = localStorage.getItem('currentInstance');

    if (instanceOnStorage !== currentInstance) {
      forcePipelineStop(router);
      return;
    }

    if (state.changed) {
      navigateToStep(router, state);
      transitionListeners.forEach(cb => cb(state));
    }
  };
}

function start(router: VueRouter, previousState?: StateConfig<Context, AnyEventObject>) {
  const loadedPreviousState = previousState ? loadPreviousState(previousState) : undefined;

  const currentInstance = String(Date.now());
  localStorage.setItem('currentInstance', currentInstance);

  const { state: currentState } = pipeline
    .onTransition(onTransitionHandler(router, currentInstance))
    .start(loadedPreviousState);

  navigateToStep(router, currentState);
}

function addTransitionListener(listener: Function, forceTrigger?: boolean) {
  const isAlreadyRegistered = transitionListeners.includes(listener);

  if (!isAlreadyRegistered) {
    if (forceTrigger) listener(pipeline.state);
    transitionListeners.push(listener);
  }
}

function removeTransitionListener(cb: Function) {
  const listenerIndex = transitionListeners.indexOf(cb);

  if (listenerIndex >= 0) {
    transitionListeners.splice(listenerIndex);
  }
}

export default {
  get currentStep() {
    return getCurrentStep(pipeline.state);
  },
  get state() {
    return pipeline.state;
  },
  get initialized() {
    return pipeline.initialized;
  },
  addTransitionListener,
  forcePipelineStop,
  goTo: (step: string) => {
    if (step) pipeline.send(`GO_TO_${step.toUpperCase()}`);
  },
  loadPreviousState,
  navigateToStep,
  next: (event?: EventData) => pipeline.send('NEXT', event),
  resetHistory: (event?: EventData) => pipeline.send('RESET_HISTORY', event),
  onTransitionHandler,
  prev: (event?: EventData) => pipeline.send('PREV', event),
  removeTransitionListener,
  send: pipeline.send,
  start,
  stop: pipeline.stop,
  transitionListeners,
  __pipeline: pipeline,
};
